import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiSearch, FiX } from "react-icons/fi";
import { MyDomain } from "../App";

function normalizeProduct(p) {
  const hasDiscount = !!(
    p.priceAfterDiscount &&
    p.price &&
    p.priceAfterDiscount < p.price
  );
  return {
    id: p.id,
    name: p.name || "",
    image: p.image
      ? p.image.startsWith("http")
        ? p.image
        : `${MyDomain}${p.image}`
      : "",
    price: hasDiscount ? p.priceAfterDiscount : p.price || 0,
    originalPrice: hasDiscount ? p.price : null,
    hasDiscount,
    discountPercent: p.discount || null,
    gender: p.gender || "",
  };
}

export default function SearchModal({ onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      setLoading(true);
      axios
        .get(`${MyDomain}Products2/SearchByName`, { params: { name: query } })
        .then((res) => setResults(res.data.map(normalizeProduct)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (id) => {
    onClose();
    navigate(`/product/${id}`);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 80,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 560,
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
          margin: "0 16px",
        }}
      >
        {/* Search input row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "14px 18px",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            gap: 10,
          }}
        >
          <FiSearch size={20} color="#888" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products by name..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 16,
              color: "#1a1a1a",
              background: "transparent",
            }}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#888",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FiX size={18} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#555",
              fontSize: 13,
              padding: "4px 8px",
              borderRadius: 6,
            }}
          >
            Cancel
          </button>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          {loading && (
            <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 14 }}>
              Searching...
            </div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 14 }}>
              No products found for "{query}"
            </div>
          )}

          {!loading &&
            results.map((p) => (
              <div
                key={p.id}
                onClick={() => handleSelect(p.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 18px",
                  cursor: "pointer",
                  borderBottom: "1px solid rgba(0,0,0,0.05)",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(0,0,0,0.04)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.name}
                    style={{
                      width: 52,
                      height: 52,
                      objectFit: "cover",
                      borderRadius: 8,
                      background: "#f5f5f5",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 8,
                      background: "#f0f0f0",
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#1a1a1a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {p.name}
                  </div>
                  <div style={{ marginTop: 4, display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                      ₪{p.price}
                    </span>
                    {p.hasDiscount && (
                      <span style={{ fontSize: 12, color: "#999", textDecoration: "line-through" }}>
                        ₪{p.originalPrice}
                      </span>
                    )}
                    {p.discountPercent && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#e11d48",
                          background: "#fff0f3",
                          padding: "1px 6px",
                          borderRadius: 4,
                        }}
                      >
                        -{p.discountPercent}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
